import type { HttpErrorResponse } from '@angular/common/http';

import { createSelector } from '@ngrx/store';

import type { Department, DepartmentPayload } from '@core/departments-types';
import type { CorrelationId } from '@core/ngrx-legacy';
import {
  createEntityCollectionStore,
  getEntityCollectionActions,
  pipeState,
} from '@core/ngrx-legacy';

import type { DepartmentIdMap } from './department.types';

const idSelector = ({ id }: { id: number }) => id;
const { actions, namespace, reducer, selectors } = createEntityCollectionStore<Department>()(
  'department',
  {
    idSelector,

    customActions: adapter => {
      const actions = getEntityCollectionActions(adapter, idSelector);

      return {
        merge: (state, payload: { ids: number[]; department: DepartmentPayload }) => state,
        mergeSuccess: (state, payload: { data: Department; mergedIds: number[] }) =>
          pipeState(
            state,
            next => adapter.removeMany(next, payload.mergedIds),
            next => adapter.addOne(next, payload.data),
            next => adapter.markAsLoaded(next, { entities: [payload.data.id], scope: null }),
          ),
        mergeFailed: (
          state,
          payload: {
            error: HttpErrorResponse;
            data: { ids: number[]; department: DepartmentPayload };
          },
        ) => state,

        deleteMany: (state, payload: { ids: number[] }) =>
          adapter.markAsPending(state, { entities: payload.ids, scope: null }),
        deleteManySuccess: (state, payload: { ids: number[] }) =>
          adapter.removeMany(state, payload.ids),
        deleteManyFailed: (state, payload: { error: HttpErrorResponse; data: { ids: number[] } }) =>
          adapter.setError(state, payload.error),
        create: (state, payload: { data: DepartmentPayload } & Required<CorrelationId>) =>
          adapter.markAsPending(state, { entities: [payload.correlationId], scope: null }),
        createSuccess: (state, payload: { data: Department } & Required<CorrelationId>) =>
          actions.createOnePessimisticSuccess(state, payload),
        createFailed: (state, payload: { error: HttpErrorResponse } & Required<CorrelationId>) =>
          actions.createOnePessimisticFailed(state, payload),
        update: (state, payload: { id: number; data: DepartmentPayload }) =>
          actions.updateOnePessimistic(state, { data: { id: payload.id } }),
        updateSuccess: (state, payload: { data: Department }) =>
          actions.updateOnePessimisticSuccess(state, { data: payload.data }),
        updateFailed: (
          state,
          payload: { error: HttpErrorResponse; data: { id: number; data: DepartmentPayload } },
        ) =>
          actions.updateOnePessimisticFailed(state, {
            data: { id: payload.data.id },
            error: payload.error,
          }),
      };
    },
  },
);

const idMap = createSelector(selectors.entities(), departments =>
  departments.reduce(
    (acc, department) => {
      acc[department.id] = department;
      return acc;
    },
    <DepartmentIdMap>{},
  ),
);

const DepartmentSelectors = {
  ...selectors,
  idMap,
};

const DepartmentActions = <
  Pick<
    typeof actions,
    | 'fetchCollection'
    | 'fetchCollectionSuccess'
    | 'fetchCollectionFailed'
    | 'create'
    | 'createSuccess'
    | 'createFailed'
    | 'update'
    | 'updateSuccess'
    | 'updateFailed'
    | 'deleteMany'
    | 'deleteManySuccess'
    | 'deleteManyFailed'
    | 'merge'
    | 'mergeSuccess'
    | 'mergeFailed'
  >
>actions;

export {
  DepartmentActions,
  namespace as DepartmentNamespace,
  reducer as DepartmentReducer,
  DepartmentSelectors,
};
